import React from 'react';
import PropTypes from 'prop-types';
import { DetailsCollapseCard } from '../../Styles';
import { get, set } from '@totalsoft/change-tracking-react';
import IconButton from 'components/common/buttons/IconButton';
import { useFieldDecimalPlacesConfig } from 'hooks';
import { Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { Grid } from '@mui/material';
import { valueOrDefault } from 'utils/functions';
import { useTranslation } from 'react-i18next';
import { getErrors, isValid } from '@totalsoft/pure-validations-react';

const LeasingIncomeSchemaEventNegotiationLimitComponent = ({ lens, userGroups, onDelete, validation, disabled }) => {
  const { t } = useTranslation();

  const { cvsChPercent, cvsChAmount } = useFieldDecimalPlacesConfig();

  const firstApprovalUserGroups = ((lens.firstApprovalUserGroups |> get) || '')
    .split(';')
    .map(code => userGroups.find(ug => ug.code === code))
    .filter(ug => Boolean(ug));

  const secondApprovalUserGroups = ((lens.secondApprovalUserGroups |> get) || '')
    .split(';')
    .map(code => userGroups.find(ug => ug.code === code))
    .filter(ug => Boolean(ug));

  const handleUserGroupsChange = property => value => {
    (lens[property] |> set)(value.map(ug => ug.code).join(';'));
  };

  const getTitle = () => {
    const orderNo = (lens.orderNo |> get) || '';
    const lowerPercentLimit = lens.lowerPercentLimit |> get;
    const lowerPercentLimitValue =
      lowerPercentLimit && typeof lowerPercentLimit === 'number' ? lowerPercentLimit.toFixed(cvsChPercent) : '-';
    const upperPercentLimit = lens.upperPercentLimit |> get;
    const upperPercentLimitValue =
      upperPercentLimit && typeof upperPercentLimit === 'number' ? upperPercentLimit.toFixed(cvsChPercent) : '-';
    return `${orderNo}. [${lowerPercentLimitValue}%, ${upperPercentLimitValue}%]`;
  };

  return (
    <DetailsCollapseCard
      title={getTitle()}
      toggleOnHeaderClick
      actions={<IconButton type='delete' color='error' onClick={() => onDelete(lens.id |> get)} disabled={disabled} />}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={2}>
          <TextField
            label={t('LeasingIncomeSchema.ProcessingOrder')}
            fullWidth
            isNumeric
            decimalScale={0}
            value={lens.orderNo |> get |> valueOrDefault('')}
            onChange={lens.orderNo |> set}
            error={!isValid(validation.orderNo)}
            helperText={getErrors(validation.orderNo)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextField
            label={t('LeasingIncomeSchema.LowerPercentLimit')}
            fullWidth
            isNumeric
            decimalScale={cvsChPercent}
            value={lens.lowerPercentLimit |> get |> valueOrDefault('')}
            onChange={lens.lowerPercentLimit |> set}
            error={!isValid(validation.lowerPercentLimit)}
            helperText={getErrors(validation.lowerPercentLimit)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextField
            label={t('LeasingIncomeSchema.UpperPercentLimit')}
            fullWidth
            isNumeric
            decimalScale={cvsChPercent}
            value={lens.upperPercentLimit |> get |> valueOrDefault('')}
            onChange={lens.upperPercentLimit |> set}
            error={!isValid(validation.upperPercentLimit)}
            helperText={getErrors(validation.upperPercentLimit)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t('LeasingIncomeSchema.LowerAmountLimit')}
            fullWidth
            isNumeric
            decimalScale={cvsChAmount}
            value={lens.lowerValueLimit |> get |> valueOrDefault('')}
            onChange={lens.lowerValueLimit |> set}
            error={!isValid(validation.lowerValueLimit)}
            helperText={getErrors(validation.lowerValueLimit)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t('LeasingIncomeSchema.UpperAmountLimit')}
            fullWidth
            isNumeric
            decimalScale={cvsChAmount}
            value={lens.upperValueLimit |> get |> valueOrDefault('')}
            onChange={lens.upperValueLimit |> set}
            error={!isValid(validation.upperValueLimit)}
            helperText={getErrors(validation.upperValueLimit)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            label={t('LeasingIncomeSchema.GroupsFirstApproval')}
            isMultiSelection
            value={firstApprovalUserGroups}
            onChange={handleUserGroupsChange('firstApprovalUserGroups')}
            options={userGroups}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12}>
          <Autocomplete
            label={t('LeasingIncomeSchema.GroupsSecondApproval')}
            isMultiSelection
            value={secondApprovalUserGroups}
            onChange={handleUserGroupsChange('secondApprovalUserGroups')}
            options={userGroups}
            disabled={disabled}
          />
        </Grid>
      </Grid>
    </DetailsCollapseCard>
  );
};

LeasingIncomeSchemaEventNegotiationLimitComponent.propTypes = {
  lens: PropTypes.object,
  userGroups: PropTypes.array,
  onDelete: PropTypes.func,
  validation: PropTypes.object,
  disabled: PropTypes.bool
};

export default LeasingIncomeSchemaEventNegotiationLimitComponent;
